import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pt-3"
}
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 1,
  class: "pt-3"
}
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { key: 3 }
const _hoisted_6 = ["value"]
const _hoisted_7 = { class: "tooltip-box" }

import type { ConfigDataProps, ExposeType } from "@/components/form";
import type { User } from "@/api/login";
import type { Result } from "@/plugins/axios";
import { useStore } from "@/store";
import { updateUserInfo } from "@/api/userCenter";
import { getUserInfo } from "@/api/login";
import { recover } from "@/plugins/keythereum";
import { ConfigProvider, message, Modal } from "ant-design-vue";
import { useRouter } from "@/router";
import { saveKeyChain } from "@/utils/hooks/systemKeychain";
import OTPInput from "@/components/OTPInput.vue";
import {
  ref,
  computed,
  reactive,
  toRaw,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { ExclamationCircleFilled } from "@ant-design/icons-vue";
import Form from "@/components/form/index.vue";
import { isRecord } from "@/utils/typeGuards";
import { useCrypto } from "@/plugins/crypto/hook";
import { t } from "@/plugins/i18n";
import { Tooltip } from "ant-design-vue";

interface Props {
  resetPassword: boolean;
  color?: string;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'getPrivateKey',
  props: {
    resetPassword: { type: Boolean },
    color: {}
  },
  emits: ["close", "screct", "password"],
  setup(__props: any, { emit: __emit }) {

const showOTPInput = computed(() => {
  return !resetPassword && !!privateKey.value;
});
/** 供密码管理器保存/填充时关联“用户名”，优先展示用户昵称（userName），无则回退用户编号（userNo） */
const usernameForPasswordManager = computed(() => {
  const info = store.state.user.userInfo as User | undefined;
  return info?.userName ?? info?.userNo ?? "";
});
const prop = __props;

const router = useRouter();

const { resetPassword, color = "#4749ad" } = toRaw(prop);
const IS_ELECTRON = process.env.IS_ELECTRON;

const codeArray = ref(Array(6).fill(""));

const handleCodeUpdate = (newCode: string[]) => {
  console.log("newCode:", newCode);
  codeArray.value = newCode;
};
console.log("color:", color);

function six(value: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (/[^0-9]/g.test(value)) {
      return reject(t("userCenter.privateKey.onlyNumbers"));
    }

    if (value && (value as string).length !== 6)
      return reject(t("userCenter.privateKey.sixDigitsRequired"));

    return resolve();
  });
}

const store = useStore();

const privateKey = computed(
  () => (store.state.user.userInfo as User)?.privateKey
);

const userId = (store.state.user.userInfo as User)?.id;

const publicKey = (store.state.user.userInfo as User)?.credential;

console.log("userinfo", store.state.user.userInfo);

const emit = __emit;

const open = ref(true);
const loading = ref(false);
const setRef = ref<ExposeType>();
const welcomeMessage = computed(() =>
  t("userCenter.privateKey.welcomeMessage")
);
const changeMessage = computed(() => t("userCenter.privateKey.changeMessage"));

// 弹窗内用 ConfigProvider 注入主题色，与「确认修改」弹窗一致：主色 + hover 均由 Ant Design 自动变浅
const modalPrimaryColor = computed(() => color || "#4749ad");

const closeOnNavigation = () => {
  open.value = false;
  emit("close");
};

const navigateToUserInfo = () => {
  if (IS_ELECTRON) {
    window.open(`${process.env.VUE_APP_H5_DOMAIN}/userCenter/userInfo`);
  } else {
    open.value = false;
    emit("close");
    router.push("/userCenter/userInfo");
  }
};

onMounted(() => {
  nextTick(() => {
    if (resetPassword || !privateKey.value) {
      const passwordInput = document.querySelector(
        'input[type="password"]'
      ) as HTMLInputElement;
      if (passwordInput) passwordInput.focus();
    } else {
      const otpInputs = document.querySelectorAll(".otp-input");
      if (otpInputs.length > 0) (otpInputs[0] as HTMLInputElement).focus();
    }
  });
  window.addEventListener("popstate", closeOnNavigation);
});
onBeforeUnmount(() => {
  window.removeEventListener("popstate", closeOnNavigation);
});

const setConfig = {
  title: resetPassword
    ? t("userCenter.privateKey.setNewPassword")
    : t("userCenter.privateKey.setPassword"),
  type: "warning",
  message: t("userCenter.privateKey.setPasswordMessage"),
  ok: async () => {
    try {
      const res = await setRef.value?.validate();
      if (!res) return;
      const { encryptMessage } = await useCrypto();
      if (
        !publicKey ||
        typeof publicKey !== "string" ||
        publicKey.length < 64
      ) {
        throw new Error(t("userCenter.privateKey.passwordSetupFailed"));
      }
      let encryptResult: string;
      try {
        encryptResult = await encryptMessage(publicKey, res.password);
      } catch (encErr: unknown) {
        log.error("encryptMessage failed", encErr);
        const resultDesc =
          isRecord(encErr) && typeof encErr.resultDesc === "string"
            ? encErr.resultDesc
            : undefined;
        throw new Error(
          resultDesc || t("userCenter.privateKey.passwordSetupFailed")
        );
      }
      log.debug(
        "getPrivateKey encrypt password res",
        encryptResult?.slice(0, 10) + "..."
      );
      await updateUserInfo({
        id: userId,
        password: encryptResult,
      });
      const user = await getUserInfo();
      (
        store as unknown as { commit: (type: string, payload: unknown) => void }
      ).commit("user/setUserInfo", user);
      const keystore = user.privateKey;
      if (!keystore)
        return Promise.reject(t("userCenter.privateKey.passwordMismatch"));
      const privateKey = recover({
        password: res.password as string,
        keystore: JSON.parse(keystore),
      });
      // 先发送密码事件，再发送私钥事件
      emit("password", res.password as string);
      emit("screct", privateKey);
      // 敏感信息已移除：decryptPasswordToMessage 结果
      if (IS_ELECTRON) {
        saveKeyChain({
          userNo: (store.state.user.userInfo as User)?.userNo,
          privateKey,
        });
      }
      message.success(t("userCenter.privateKey.passwordSetSuccess"));
      return;
    } catch (e: unknown) {
      const errorFields =
        isRecord(e) && Array.isArray(e.errorFields) ? e.errorFields : undefined;
      if (errorFields && errorFields.length > 0) {
        return;
      }
      message.error(
        ((isRecord(e) && typeof e.resultDesc === "string"
          ? e.resultDesc
          : undefined) as Result["resultDesc"]) ||
          t("userCenter.privateKey.passwordSetupFailed")
      );
      log.error(e);
    }
  },
  cancel: async () => {
    open.value = false;
    emit("close");
  },
};

const setform = reactive<ConfigDataProps>({
  password: {
    label: t("userCenter.privateKey.setPassword"),
    value: undefined,
    antType: "inputPassword",
    rules: [
      {
        required: true,
        validator: async (_: unknown, value: string) => six(value),
        trigger: "blur",
      },
    ],
    bind: {
      placeholder: t("userCenter.privateKey.enterSixDigitPassword"),
      autocomplete: "new-password",
      name: "new-password",
      maxlength: 6,
    },
  },
  repassword: {
    label: t("userCenter.privateKey.confirmPassword"),
    value: undefined,
    antType: "inputPassword",
    rules: [
      {
        required: true,
        validator: async (_: unknown, value: string) => {
          await six(value as string);
          if (value !== setform.password.value) {
            return Promise.reject(t("userCenter.privateKey.passwordMismatch"));
          }
          return Promise.resolve();
        },
        trigger: "blur",
      },
    ],
    bind: {
      placeholder: t("userCenter.privateKey.enterSixDigitPassword"),
      autocomplete: "new-password",
      name: "confirm-password",
      maxlength: 6,
    },
  },
});

const putConfig = {
  title: t("userCenter.privateKey.enterPassword"),
  type: "info",
  message: t("userCenter.privateKey.enterPasswordMessage"),
  ok: async () => {
    try {
      const password = codeArray.value.join("");
      if (!password) {
        message.error(t("userCenter.privateKey.pleaseEnterPassword"));
        return;
      }
      if (password.length !== 6) {
        message.error(t("userCenter.privateKey.sixDigitsRequired"));
        return;
      }

      const res = recover({
        password,
        keystore: JSON.parse(privateKey.value || "{}"),
      });
      emit("password", password);
      emit("screct", res);
      if (IS_ELECTRON) {
        saveKeyChain({
          userNo: (store.state.user.userInfo as User)?.userNo,
          privateKey: res,
        });
      }
    } catch (e) {
      message.error(t("userCenter.privateKey.incorrectPassword"));
    }
  },
  cancel: async () => {
    open.value = false;
    emit("close");
  },
};

const config = computed((() =>
  !resetPassword && privateKey.value ? putConfig : setConfig) as () => {
  title: string;
  type: "info" | "warning";
  message: string;
  ok: () => Promise<void>;
  cancel: () => Promise<void>;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(ConfigProvider), {
      theme: { token: { colorPrimary: modalPrimaryColor.value } }
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Modal), {
          open: open.value,
          "onUpdate:open": _cache[0] || (_cache[0] = ($event: any) => ((open).value = $event)),
          title: config.value?.title,
          maskClosable: false,
          confirmLoading: loading.value,
          onOk: config.value?.ok,
          onCancel: config.value?.cancel,
          "ok-text": _unref(t)('userCenter.confirm'),
          "cancel-text": _unref(t)('userCenter.cancel'),
          class: _normalizeClass(`${showOTPInput.value ? 'otp-modal' : ''}`)
        }, {
          default: _withCtx(() => [
            (!privateKey.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_1, [
                  _createVNode(_unref(ExclamationCircleFilled), { style: {"color":"#4749ad"} }),
                  _createElementVNode("span", {
                    class: "ml-2",
                    innerHTML: welcomeMessage.value
                  }, null, 8, _hoisted_2)
                ]))
              : (_unref(resetPassword))
                ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                    _createVNode(_unref(ExclamationCircleFilled), { style: {"color":"#4749ad"} }),
                    _createElementVNode("span", {
                      class: "ml-2",
                      innerHTML: changeMessage.value
                    }, null, 8, _hoisted_4)
                  ]))
                : _createCommentVNode("", true),
            (_unref(resetPassword) || !privateKey.value)
              ? (_openBlock(), _createBlock(Form, {
                  key: 2,
                  class: "content",
                  ref_key: "setRef",
                  ref: setRef,
                  "config-data": setform,
                  autocomplete: "on",
                  configLayout: { labelCol: { span: 0 }, wrapperCol: { span: 24 } }
                }, null, 8, ["config-data"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("input", {
                    type: "text",
                    autocomplete: "username",
                    name: "username",
                    value: usernameForPasswordManager.value,
                    class: "otp-hidden-username",
                    tabindex: "-1",
                    "aria-hidden": "true"
                  }, null, 8, _hoisted_6),
                  _createVNode(OTPInput, {
                    "onUpdate:code": handleCodeUpdate,
                    color: _unref(color)
                  }, null, 8, ["color"]),
                  _createVNode(_unref(Tooltip), { placement: "rightTop" }, {
                    title: _withCtx(() => [
                      _createElementVNode("div", null, [
                        _createTextVNode(_toDisplayString(_unref(t)("userCenter.privateKey.authPasswordDescription")) + " ", 1),
                        _createElementVNode("a", {
                          style: { color: '#797DFF', cursor: 'pointer' },
                          onClick: navigateToUserInfo
                        }, _toDisplayString(_unref(t)("userCenter.privateKey.personalInfoLink")), 1),
                        _createTextVNode(" " + _toDisplayString(_unref(t)("userCenter.privateKey.resetInstructions")), 1)
                      ])
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("p", _hoisted_7, [
                        _createVNode(_unref(ExclamationCircleFilled), {
                          style: _normalizeStyle({
                  marginRight: '4px',
                  color: _unref(color) === '#4749ad' ? _unref(color) : '#ff9432',
                })
                        }, null, 8, ["style"]),
                        _createElementVNode("span", null, _toDisplayString(_unref(t)("userCenter.privateKey.whatIsAuthPassword")), 1)
                      ])
                    ]),
                    _: 1
                  })
                ]))
          ]),
          _: 1
        }, 8, ["open", "title", "confirmLoading", "onOk", "onCancel", "ok-text", "cancel-text", "class"])
      ]),
      _: 1
    }, 8, ["theme"])
  ]))
}
}

})